// src/pages/Users/News/components/NewsCard.jsx

import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaCalendarAlt,
  FaEdit,
  FaEye,
  FaShareAlt,
  FaTimes,
  FaTrash,
  FaUserEdit,
} from "react-icons/fa";

import { deleteNews } from "../../../../services/newsServices";

import NewsFullModal from "../NewsFullModal";

import {
  FALLBACK_NEWS_IMG,
  formatNewsDate,
  getAuthorName,
  getNewsCover,
  getNewsDate,
  sanitizeLight,
  truncateText,
} from "../utils/newsUtils";

function onImgError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_NEWS_IMG;
  }
}

export default function NewsCard({
  news,
  canModify,
  onDeleted,
  onEdited,
  onEdit,
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const cover = getNewsCover(news);
  const author = getAuthorName(news);
  const date = formatNewsDate(getNewsDate(news));

  const excerpt = useMemo(
    () => truncateText(news?.content || "", 210),
    [news?.content]
  );

  const share = async () => {
    try {
      const url = `${window.location.origin}${window.location.pathname}#news-${news.id}`;
      await navigator.clipboard.writeText(url);
      alert("Lien copié dans le presse-papiers.");
    } catch {
      alert("Impossible de copier le lien.");
    }
  };

  const remove = async () => {
    try {
      await deleteNews(news.id);
      onDeleted?.();
      onEdited?.();
    } catch (error) {
      console.error("Suppression actualité échouée", error);
      alert("Erreur lors de la suppression.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <article
      id={`news-${news.id}`}
      className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900"
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative block w-full overflow-hidden bg-slate-100 text-left dark:bg-slate-950"
        aria-label={`Lire ${news?.title || "actualité"}`}
      >
        <div className="aspect-[16/10] w-full">
          <img
            src={cover}
            alt={news?.title || "Illustration"}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
            loading="lazy"
            onError={onImgError}
          />
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-transparent to-transparent" />

        <div className="absolute left-4 top-4">
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-950 shadow-sm">
            <FaCalendarAlt className="text-orange-500" />
            {date}
          </span>
        </div>

        {!news?.is_active && (
          <div className="absolute right-4 top-4">
            <span className="inline-flex rounded-full bg-amber-400 px-3 py-1.5 text-xs font-bold uppercase text-amber-950 shadow-sm">
              Inactif
            </span>
          </div>
        )}

        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="line-clamp-2 text-2xl font-bold leading-tight text-white drop-shadow-sm">
            {news?.title || "Sans titre"}
          </h3>
        </div>
      </button>

      <div className="p-5">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:ring-slate-800">
          <FaUserEdit />
          {author}
        </div>

        <div className="min-h-[92px]">
          {excerpt ? (
            <p className="line-clamp-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {excerpt}
            </p>
          ) : (
            <p className="text-sm italic leading-7 text-slate-400">
              Contenu non renseigné.
            </p>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-xs font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
            >
              <FaEye />
              Lire
            </button>

            <button
              type="button"
              onClick={share}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaShareAlt />
              Partager
            </button>
          </div>

          {canModify && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onEdit?.(news)}
                className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
                title="Modifier"
                aria-label="Modifier"
              >
                <FaEdit />
              </button>

              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 text-red-700 transition active:scale-95 dark:bg-red-950/30 dark:text-red-300"
                title="Supprimer"
                aria-label="Supprimer"
              >
                <FaTrash />
              </button>
            </div>
          )}
        </div>
      </div>

      {open && <NewsFullModal news={news} onClose={() => setOpen(false)} />}

      {confirming &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-sm"
            onClick={(event) =>
              event.currentTarget === event.target && setConfirming(false)
            }
            role="dialog"
            aria-modal="true"
            aria-label="Confirmer la suppression"
          >
            <div
              className="w-full max-w-md overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-rose-700 via-pink-700 to-orange-600 px-5 py-4 text-white">
                <h4 className="font-bold">Supprimer cette actualité ?</h4>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 transition hover:bg-white/25"
                  aria-label="Fermer"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="p-5">
                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Cette action est définitive. L’actualité sera supprimée du fil
                  du club.
                </p>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
                  >
                    Annuler
                  </button>

                  <button
                    type="button"
                    onClick={remove}
                    className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-rose-600 px-4 text-sm font-bold text-white transition active:scale-95"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </article>
  );
}